import React, { ReactElement, lazy } from 'react';
import './styles-overrides.css';
import { ParentProvider } from '@paypalcorp/quantum-leap-component-kit/components/ParentProvider';
import { CMSPage } from '@paypalcorp/cms-engine/components';
import { RegionalBannerFragment } from '@paypalcorp/regionalbannersdk/RegionalBannerFragment';
import { NavHeader, NavFooter } from '@paypalcorp/axo-component-kit';
import { overrideEntryPathForMPP } from '../../utils/commonUtils';
import { CustomAppProps } from '../types/appTypes';

function createCssWrapperComponent({
  ComponentRef, props, cssList, componentResolutionMetaData, nonce, criticality,
} :
  {
  ComponentRef: React.ComponentType<any>,
  props :any,
  cssList : string[][],
  componentResolutionMetaData : Record< string, string>,
  nonce : string,
  criticality : 'critical'| 'non-critical'
 }) {
  // exclude styles that are already included in page's body HTML
  const refinedCssList : string[] = [];
  cssList?.forEach(([key, value]) => {
    if (!componentResolutionMetaData[key]) {
      refinedCssList.push(value);
    }
  });
  return (
    <>
      {/* Dynamically generate CSS link tags for dependent CSS files */}
      {/* eslint-disable react/no-unknown-property */}
      {refinedCssList.map((cssFile) => (
        <link rel="stylesheet" nonce={nonce} href={cssFile} key={cssFile} fetchPriority={criticality === 'critical' ? 'high' : 'low'} />
      ))}
      <ComponentRef {...props} />
    </>
  );
}

export default function App({
  renderContext,
}: Readonly<CustomAppProps>) {
  const {
    componentKit, pageContext, pageModel, componentResolutionMetaData = {}, ...props
  } = renderContext;
  const ComponentsMap: Record<string, ReactElement | any> = {};
  try {
    Object.keys(componentKit).forEach((key) => {
      ComponentsMap[key] = componentKit[key]?.bundle || lazy(() => import(`../../../node_modules/@paypalcorp/axo-component-kit/dist/components/${key}.js`));
    });
  } catch (error) {
    console.error('Error loading components: ', error);
  }

  const {
    cmsEngineContext: {
      environment: {
        isRTL = false,
      } = {},
    } = {},
    additionalContext: {
      fastlaneNavComponents = [],
    } = {},
  } = pageContext;

  const header = fastlaneNavComponents?.find((item) => item?.componentType === 'NavHeader');
  const footer = fastlaneNavComponents?.find((item) => item?.componentType === 'NavFooter');

  // dynamic import of NavHeader and NavFooter js scripts
  // eslint-disable-next-line
  ComponentsMap.NavHeader = header?.bundle || lazy(() => import('../../../node_modules/@paypalcorp/axo-component-kit/dist/components/NavHeader.js'));
  // eslint-disable-next-line
  ComponentsMap.NavFooter = footer?.bundle || lazy(() => import('../../../node_modules/@paypalcorp/axo-component-kit/dist/components/NavFooter.js'));

  const headerWrapper = createCssWrapperComponent({
    ComponentRef: NavHeader,
    props: header?.model,
    cssList: header?.cssBundle,
    componentResolutionMetaData,
    nonce: header?.nonce,
    criticality: 'critical',
  });

  const footerWrapper = createCssWrapperComponent({
    ComponentRef: NavFooter,
    props: footer?.model,
    cssList: footer?.cssBundle,
    componentResolutionMetaData,
    nonce: footer?.nonce,
    criticality: 'non-critical',
  });

  return (
    <CMSPage
      data-testid="fastlane-cms-page"
      componentKit={{
        Parent: ParentProvider,
        ...ComponentsMap,
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
      hostedBy="ppcmsnodeweb"
      pageContext={overrideEntryPathForMPP(pageContext)}
      pageModel={pageModel}
      {...props}
      render={({
        aboveHeader, body, belowFooter,
      }) => (
        <>
          <RegionalBannerFragment />
          {headerWrapper}
          {aboveHeader}
          <div className="document-body">
            {body}
          </div>
          {footerWrapper}
          {belowFooter}
        </>
      )}
    />
  );
}
